import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, ListFilter } from "lucide-react";
import { searchPrograms } from "../lib/api";

/**
 * Sticky bar shown on the Program page when the user navigated there from a
 * search result (URL contains ?q=...&i=...). Re-runs the same search to
 * rebuild the hit list, then lets the user step through it with prev / next
 * buttons or the ← / → keys without losing their place.
 */
export default function SearchResultsNav() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const q = params.get("q");
    const i = parseInt(params.get("i") || "-1", 10);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(Boolean(q));

    // Build a stable cache key from the relevant search params so we only
    // re-fetch when the query / filters actually change.
    const fetchKey = useMemo(() => {
        if (!q) return "";
        const o = { q };
        ["period", "type"].forEach((k) => {
            const v = params.get(k);
            if (v) o[k] = v;
        });
        return JSON.stringify(o);
    }, [q, params]);

    useEffect(() => {
        if (!q) {
            setResults([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        const o = JSON.parse(fetchKey);
        let cancelled = false;
        searchPrograms(o)
            .then((d) => {
                if (!cancelled) setResults(d.results || []);
            })
            .catch(() => {
                if (!cancelled) setResults([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [fetchKey, q]);

    const buildUrl = useCallback(
        (idx) => {
            const r = results[idx];
            if (!r) return null;
            const next = new URLSearchParams(params);
            next.set("i", String(idx));
            return `/programs/${r.program_id}?${next.toString()}#p-${r.paragraph_id}`;
        },
        [results, params],
    );

    const goPrev = useCallback(() => {
        if (i > 0) {
            const url = buildUrl(i - 1);
            if (url) navigate(url);
        }
    }, [i, buildUrl, navigate]);

    const goNext = useCallback(() => {
        if (i >= 0 && i < results.length - 1) {
            const url = buildUrl(i + 1);
            if (url) navigate(url);
        }
    }, [i, results.length, buildUrl, navigate]);

    // Keyboard shortcuts: ← previous, → next, Esc back to list
    useEffect(() => {
        if (!q || i < 0) return undefined;
        const handler = (e) => {
            const target = e.target;
            if (
                target &&
                ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)
            )
                return;
            if (e.key === "ArrowLeft") {
                e.preventDefault();
                goPrev();
            } else if (e.key === "ArrowRight") {
                e.preventDefault();
                goNext();
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [q, i, goPrev, goNext]);

    if (!q || i < 0) return null;

    const backToSearch = `/search?${params.toString()}`;
    const total = results.length;
    const hasPrev = i > 0;
    const hasNext = i >= 0 && i < total - 1;

    return (
        <div
            data-testid="search-results-nav"
            className="sticky top-[73px] z-40 -mx-6 mb-8 border-b border-zinc-200 bg-amber-50/95 px-6 py-3 backdrop-blur md:-mx-12 md:px-12"
        >
            <div className="flex flex-wrap items-center justify-between gap-3">
                <Link
                    to={backToSearch}
                    data-testid="back-to-search"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-700 hover:text-zinc-950"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Tilbake til søk
                </Link>

                <div className="flex items-center gap-3">
                    <span className="pp-overline">
                        <ListFilter className="mr-1 inline h-3 w-3" />
                        {loading
                            ? "Laster treff…"
                            : total > 0
                              ? `Treff ${i + 1} av ${total}`
                              : "Ingen treff"}
                        {q && (
                            <span className="ml-2 normal-case text-zinc-600">
                                for «<span className="font-semibold">{q}</span>»
                            </span>
                        )}
                    </span>
                    <div className="flex items-center">
                        <button
                            type="button"
                            data-testid="nav-prev-result"
                            onClick={goPrev}
                            disabled={!hasPrev}
                            aria-label="Forrige treff (← venstre piltast)"
                            title="Forrige treff (← venstre piltast)"
                            className="flex h-9 w-9 items-center justify-center border border-zinc-300 bg-white text-zinc-700 transition-colors hover:border-zinc-950 hover:text-zinc-950 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-zinc-300 disabled:hover:text-zinc-700"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            data-testid="nav-next-result"
                            onClick={goNext}
                            disabled={!hasNext}
                            aria-label="Neste treff (→ høyre piltast)"
                            title="Neste treff (→ høyre piltast)"
                            className="-ml-px flex h-9 w-9 items-center justify-center border border-zinc-300 bg-white text-zinc-700 transition-colors hover:border-zinc-950 hover:text-zinc-950 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-zinc-300 disabled:hover:text-zinc-700"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
